import { VariantProps, cva } from 'class-variance-authority';
import { MaskitoOptions } from '@maskito/core';
import { mergeRefs } from 'react-merge-refs';
import { useMaskito } from '@maskito/react';
import * as React from 'react';

import { cn } from '../../utils';

export interface InputProps
  extends React.ComponentProps<'input'>,
    VariantProps<typeof inputVariants> {
  trailingIcon?: {
    position: 'right' | 'left';
    icon: React.ReactNode;
    onClick?: () => void;
  };
  required?: boolean;
  maskOptions?: MaskitoOptions;
  placeholder?: string;
  floating?: boolean;
  readonly?: boolean;
  name: string;
}

export const inputVariants = cva(
  `flex h-14 px-3 w-full peer rounded-md outline outline-border-default bg-background
   sm:text-sm text-sm
   disabled:text-muted-foreground disabled:outline-border-disabled disabled:cursor-not-allowed disabled:bg-body disabled:outline-none disabled:text-content-disabled
   focus:outline-surface focus:outline-1 focus:ring-3 focus:ring-border-sky focus:ring-offset-0
   hover:outline-border-hover`,
  {
    variants: {
      inputSize: {
        small: 'h-10',
        medium: 'h-12',
        large: 'h-14 rounded-lg',
      },
      hasError: {
        true: 'outline-critical',
      },
      iconPosition: {
        right: 'pr-9',
        left: 'pl-9',
      },
      floating: {
        true: 'pt-6 placeholder:text-transparent',
        false: '[&::placeholder]:text-weak',
      },
      hasShadow: { true: 'shadow-sm disabled:shadow-none' },
    },
    compoundVariants: [
      { floating: true, inputSize: 'small', class: 'pt-2' },
      { floating: true, inputSize: 'medium', class: 'pt-3' },
      { floating: true, inputSize: 'large', class: 'pt-4' },
    ],
    defaultVariants: {
      inputSize: 'medium',
      floating: false,
      hasError: false,
      hasShadow: false,
    },
  },
);

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      placeholder = '',
      floating = false,
      trailingIcon,
      maskOptions,
      className,
      inputSize,
      hasError,
      required,
      type,
      name,
      hasShadow,
      ...props
    },
    ref,
  ) => {
    const inputMask = useMaskito({ options: maskOptions });
    const inputRef = mergeRefs([ref, inputMask]);

    return (
      <div className="relative">
        <input
          className={cn(
            inputVariants({
              iconPosition: trailingIcon?.position,
              className,
              inputSize,
              hasError,
              floating,
              hasShadow,
            }),
          )}
          ref={inputRef as React.Ref<HTMLInputElement> | undefined}
          placeholder={placeholder}
          type={type}
          name={name}
          id={name}
          {...props}
        />

        {placeholder && floating && (
          <InputPlaceholder
            iconOffset={trailingIcon?.position === 'left'}
            placeholder={placeholder}
            required={required}
            size={inputSize}
            name={name}
          />
        )}

        {trailingIcon && (
          <div
            className={cn('absolute -translate-y-1/2 top-[50%]', {
              'left-3': trailingIcon.position === 'left',
              'right-3': trailingIcon.position === 'right',
            })}
            onClick={trailingIcon.onClick}
          >
            {trailingIcon.icon}
          </div>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };

export const inputPlaceholderVariants = cva(
  'z-10 absolute text-sm text-weak duration-300 left-3 transform -translate-y-2.5 scale-75 top-4.5 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-2.5 peer-disabled:z-0 peer-disabled:text-content-disabled cursor-text peer-autofill:scale-75',
  {
    variants: {
      size: {
        small: 'top-2.5',
        medium: 'top-3.5',
        large: 'top-4.5',
      },
      iconOffset: {
        true: 'left-12',
      },
      required: {
        true: "after:content-['*'] after:text-critical",
      },
    },
    defaultVariants: {
      size: 'medium',
      required: false,
    },
  },
);

interface InputPlaceholderProps extends VariantProps<typeof inputPlaceholderVariants> {
  placeholder?: string;
  name: string;
}

const InputPlaceholder = ({
  name,
  placeholder,
  required,
  iconOffset,
  size,
}: InputPlaceholderProps) => {
  return (
    <label className={cn(inputPlaceholderVariants({ iconOffset, size, required }))} htmlFor={name}>
      {placeholder}
    </label>
  );
};
